export default class ProductCarousel extends HTMLElement {
  private grid: HTMLElement | null = null;
  private items: HTMLElement[] = [];
  private nav: HTMLElement | null = null;
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;
  private currentIndex = 0;
  private initialized = false;

  private onScrollBound = this.onScroll.bind(this);
  private onResizeBound = this.onResize.bind(this);
  private onPrevButtonClickBound = this.onPrevButtonClick.bind(this);
  private onNextButtonClickBound = this.onNextButtonClick.bind(this);

  connectedCallback(): void {
    this.refresh();
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.initialized = false;
  }

  refresh(): void {
    this.grid = this.querySelector('[data-product-carousel-ref="grid"]');
    this.nav = this.querySelector('[data-product-carousel-ref="nav"]');
    this.prevButton = this.querySelector('[data-product-carousel-ref="prev-button"]');
    this.nextButton = this.querySelector('[data-product-carousel-ref="next-button"]');
    this.items = Array.from(this.querySelectorAll('[data-product-carousel-ref="item"]'));

    if (!this.grid || this.items.length === 0) {
      return;
    }

    if (this.initialized) {
      this.removeEventListeners();
    }

    this.addEventListeners();
    this.initialized = true;
    this.currentIndex = 0;
    this.scrollToItem(0, false);
    this.updateNavVisibility();
    this.updateButtons();
  }

  private addEventListeners(): void {
    this.grid?.addEventListener('scroll', this.onScrollBound, { passive: true });
    window.addEventListener('resize', this.onResizeBound);
    this.prevButton?.addEventListener('click', this.onPrevButtonClickBound);
    this.nextButton?.addEventListener('click', this.onNextButtonClickBound);
  }

  private removeEventListeners(): void {
    this.grid?.removeEventListener('scroll', this.onScrollBound);
    window.removeEventListener('resize', this.onResizeBound);
    this.prevButton?.removeEventListener('click', this.onPrevButtonClickBound);
    this.nextButton?.removeEventListener('click', this.onNextButtonClickBound);
  }

  private getClosestItemIndex(): number {
    if (!this.grid || this.items.length === 0) return 0;

    const { scrollLeft, clientWidth, scrollWidth } = this.grid;
    const maxScrollLeft = scrollWidth - clientWidth;

    if (maxScrollLeft <= 0) return 0;
    if (scrollLeft >= maxScrollLeft - 1) return this.items.length - 1;
    if (scrollLeft <= 1) return 0;

    let closestIndex = 0;
    let closestDistance = Infinity;

    this.items.forEach((item, index) => {
      const targetScroll = this.getScrollLeftForItem(item);
      const distance = Math.abs(targetScroll - scrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  private getScrollLeftForItem(item: HTMLElement): number {
    if (!this.grid) return 0;

    const scrollPadding = parseFloat(getComputedStyle(this.grid).scrollPaddingLeft) || 0;

    return item.offsetLeft - scrollPadding;
  }

  private canScrollPrev(): boolean {
    return (this.grid?.scrollLeft ?? 0) > 1;
  }

  private canScrollNext(): boolean {
    if (!this.grid) return false;

    const maxScrollLeft = this.grid.scrollWidth - this.grid.clientWidth;

    return this.grid.scrollLeft < maxScrollLeft - 1;
  }

  private hasGridScroll(): boolean {
    if (!this.grid) return false;

    return this.grid.scrollWidth > this.grid.clientWidth + 1;
  }

  private updateNavVisibility(): void {
    if (!this.nav) return;

    const hasScroll = this.hasGridScroll();

    this.nav.classList.toggle('hidden', !hasScroll);
    this.nav.classList.toggle('flex', hasScroll);
  }

  private updateButtons(): void {
    const canScrollPrev = this.canScrollPrev();
    const canScrollNext = this.canScrollNext();

    this.prevButton?.classList.toggle('opacity-50', !canScrollPrev);
    this.prevButton?.classList.toggle('cursor-pointer', canScrollPrev);
    if (!canScrollPrev) {
      this.prevButton?.setAttribute('disabled', 'true');
    } else {
      this.prevButton?.removeAttribute('disabled');
    }
    this.prevButton?.setAttribute('aria-disabled', !canScrollPrev ? 'true' : 'false');

    this.nextButton?.classList.toggle('opacity-50', !canScrollNext);
    this.nextButton?.classList.toggle('cursor-pointer', canScrollNext);
    if (!canScrollNext) {
      this.nextButton?.setAttribute('disabled', 'true');
    } else {
      this.nextButton?.removeAttribute('disabled');
    }
    this.nextButton?.setAttribute('aria-disabled', !canScrollNext ? 'true' : 'false');
  }

  private scrollToItem(index: number, animate: boolean): void {
    if (!this.grid) return;

    const item = this.items[index];
    if (!item) return;

    this.grid.scrollTo({
      left: this.getScrollLeftForItem(item),
      behavior: animate ? 'smooth' : 'instant',
    });

    this.currentIndex = index;
  }

  private onPrevButtonClick(): void {
    if (!this.canScrollPrev()) return;

    this.scrollToItem(this.currentIndex - 1, true);
  }

  private onNextButtonClick(): void {
    if (!this.canScrollNext()) return;

    this.scrollToItem(this.currentIndex + 1, true);
  }

  private onScroll(): void {
    this.currentIndex = this.getClosestItemIndex();
    this.updateButtons();
  }

  private onResize(): void {
    this.updateNavVisibility();
    this.scrollToItem(this.currentIndex, false);
    this.updateButtons();
  }
}

customElements.define('product-carousel', ProductCarousel);
